// Saved items (wishlist): hearts on product cards and the product page, a header
// count, and the /pages/saved list. Items live in this browser's localStorage, so
// no account is needed; the saved page re-fetches each product for current price
// and availability. Adapted from kidify.co's kidify-shelf.js.

const STORAGE_KEY = 'randoseru:saved';
const LIMIT = 60;
const CHANGE_EVENT = 'saved-items:change';

/** @returns {Array<{handle: string, title?: string, url?: string, image?: string, price?: string, vendor?: string, at?: number}>} */
function readSaved() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeSaved(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, LIMIT)));
  } catch {
    // Storage full or blocked (private mode): the heart still toggles for this page view.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

const savedItems = {
  list: readSaved,
  has: (handle) => readSaved().some((item) => item.handle === handle),
  add(item) {
    if (!item?.handle) return;
    const list = readSaved().filter((entry) => entry.handle !== item.handle);
    list.unshift({ ...item, at: Date.now() });
    writeSaved(list);
  },
  remove(handle) {
    writeSaved(readSaved().filter((entry) => entry.handle !== handle));
  },
};

// Keep other open tabs in sync.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});

const HEART =
  '<svg class="saved-item__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 8 3.4 4.5 6.9 4.5c2 0 3.5 1.1 4.3 2.4h1.6c.8-1.3 2.3-2.4 4.3-2.4 3.5 0 5.5 3.5 4.2 6.8-1.8 4.6-9.3 9.2-9.3 9.2Z" stroke-linejoin="round"/></svg>';

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Heart toggle. Product details come from data attributes rendered by snippets/save-button.liquid. */
class SavedItemButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.#toggle);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #toggle = (event) => {
    // Hearts sit inside product-card links and zoomable media; don't navigate or zoom.
    event.preventDefault();
    event.stopPropagation();
    const { handle, title, url, image, price, vendor } = this.dataset;
    if (savedItems.has(handle)) {
      savedItems.remove(handle);
    } else {
      savedItems.add({ handle, title, url, image, price, vendor });
      this.button.classList.remove('is-popped');
      void this.button.offsetWidth;
      this.button.classList.add('is-popped');
    }
  };

  #sync = () => {
    const saved = savedItems.has(this.dataset.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label) this.button.setAttribute('aria-label', label);
  };
}

/** Header badge showing how many items are saved. */
class SavedItemsCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #sync = () => {
    const count = savedItems.list().length;
    this.textContent = count > 99 ? '99+' : String(count);
    this.hidden = count === 0;
    const link = this.closest('a');
    const base = link?.dataset.label;
    if (link && base) link.setAttribute('aria-label', count ? `${base} (${count})` : base);
  };
}

/**
 * Formats cents with the shop's money format string, e.g. "${{amount}} SGD".
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const group = (amount, decimals, thousands, decimal) => {
    const [whole, fraction] = amount.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    switch (kind) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

function sizedImage(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/** The /pages/saved grid. Renders from storage, refreshing each product from /products/{handle}.js. */
class SavedItemsList extends HTMLElement {
  #rendering = null;

  connectedCallback() {
    this.list = this.querySelector('[data-saved-list]');
    this.empty = this.querySelector('[data-saved-empty]');
    document.addEventListener(CHANGE_EVENT, this.#render);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#render);
  }

  #render = async () => {
    const entries = savedItems.list().filter((entry) => entry.handle);
    const token = Symbol('render');
    this.#rendering = token;

    if (!entries.length) {
      this.#show([]);
      return;
    }

    const root = window.Shopify?.routes?.root ?? '/';
    const fresh = await Promise.all(
      entries.map((entry) =>
        fetch(`${root}products/${encodeURIComponent(entry.handle)}.js`)
          .then((response) => {
            if (response.status === 404) return { gone: true, entry };
            return response.ok ? response.json().then((product) => ({ product, entry })) : { entry };
          })
          .catch(() => ({ entry }))
      )
    );

    // A newer render (another save/remove) superseded this one.
    if (this.#rendering !== token) return;
    for (const result of fresh) if (result.gone) savedItems.remove(result.entry.handle);
    this.#show(fresh.filter((result) => !result.gone));
  };

  #show(results) {
    const { moneyFormat = '${{amount}}', labelRemove = 'Remove', labelSoldOut = 'Sold out' } = this.dataset;
    const cards = results.map(({ product, entry }) => {
      const title = product?.title ?? entry.title;
      const url = product?.url ?? entry.url ?? `/products/${entry.handle}`;
      const image = product?.featured_image ?? entry.image;
      const vendor = product?.vendor ?? entry.vendor;
      const price = product ? formatMoney(product.price, moneyFormat) : entry.price;
      const soldOut = product ? product.available === false : false;
      return `<li class="saved-items__item">
          <a class="saved-items__card" href="${escapeHtml(url)}">
            <span class="saved-items__media">
              ${image ? `<img src="${escapeHtml(sizedImage(image, 480))}" alt="" loading="lazy" width="480" height="480">` : ''}
              ${soldOut ? `<span class="saved-items__tag">${escapeHtml(labelSoldOut)}</span>` : ''}
            </span>
            ${vendor ? `<span class="saved-items__vendor">${escapeHtml(vendor)}</span>` : ''}
            <span class="saved-items__title">${escapeHtml(title)}</span>
            ${price ? `<span class="saved-items__price">${escapeHtml(price)}</span>` : ''}
          </a>
          <saved-item-button class="saved-item saved-item--card" data-handle="${escapeHtml(entry.handle)}" data-label-saved="${escapeHtml(labelRemove)}">
            <button type="button" class="saved-item__button" aria-pressed="true" aria-label="${escapeHtml(labelRemove)}">${HEART}</button>
          </saved-item-button>
        </li>`;
    });

    if (this.list) this.list.innerHTML = cards.join('');
    if (this.empty) this.empty.hidden = cards.length > 0;
  }
}

if (!customElements.get('saved-item-button')) customElements.define('saved-item-button', SavedItemButton);
if (!customElements.get('saved-items-count')) customElements.define('saved-items-count', SavedItemsCount);
if (!customElements.get('saved-items-list')) customElements.define('saved-items-list', SavedItemsList);
